// ═══════════════════════════════════════════════════════════════════════
// Dispositifs sociaux — choix du compte de trésorerie, du mode de paiement et, pour un chèque, du chèque DISPONIBLE
// du compte (GET /comptes-tresorerie/:id/cheques?statut=DISPONIBLE, DM-WEB-002 n° 2). Partagé par la préparation
// d'un paiement en trésorerie, du paiement d'une aide et de l'acompte d'une commande de campagne.
// Le serveur reste juge : espèces par une caisse, autres modes par une banque, chèque du compte et disponible.
// ═══════════════════════════════════════════════════════════════════════
import { Champ } from './commun';
import { toArray } from './api';
import { useSocial, MODES_PAIEMENT } from './apiSocial';
import { useLectureSociale } from './communSocial';

/** Modes possibles pour un compte : caisse → espèces ; banque → virement, chèque, mobile money, autre. */
export const modesDuCompte = (c) => (c ? (c.type === 'CAISSE' ? ['ESPECES'] : ['VIREMENT', 'CHEQUE', 'MOBILE_MONEY', 'AUTRE']) : []);
/** Saisie complète : compte choisi, mode admis, chèque désigné si (et seulement si) le mode est « chèque ». */
export const paiementComplet = ({ compte, mode, cheque }) => !!(compte && modesDuCompte(compte).includes(mode) && (mode !== 'CHEQUE' || cheque));
/** Champs du corps de la préparation. */
export const champsPaiement = ({ compte, mode, cheque }) => ({ mode, compte_tresorerie_id: compte.id, ...(mode === 'CHEQUE' && cheque ? { cheque_id: cheque } : {}) });

function ChoixCheque({ compte, valeur, onChange }) {
  const api = useSocial();
  const l = useLectureSociale(() => api.cheques(compte.id, { statut: 'DISPONIBLE', taille: 200 }), [api, compte.id]);
  const cheques = toArray(l.donnees);
  const aide = l.erreur ? l.erreur.message
    : l.donnees && !cheques.length ? 'Aucun chèque disponible sur ce compte : le trésorier enregistre un chéquier (onglet Trésorerie, « Chèques »).'
      : 'Le chèque est réservé à la préparation et marqué émis à l\'exécution.';
  return (
    <Champ libelle="Chèque" requis aide={aide}>
      <select className="rh-champ-saisie" value={valeur} onChange={(e) => onChange(e.target.value)} disabled={!cheques.length}>
        <option value="">{l.donnees ? 'Choisir un chèque…' : 'Chargement…'}</option>
        {cheques.map((c) => <option key={c.id} value={c.id}>Chéquier {c.chequier} — n° {c.numero}</option>)}
      </select>
    </Champ>
  );
}

/**
 * Compte, mode et chèque. `valeur` = { compteId, mode, cheque } ; `comptes` = comptes ACTIFS ; `lecture` = lecture de la
 * trésorerie (erreur affichée sous le compte).
 */
export function ChoixCompteMode({ comptes, lecture, valeur, onChange }) {
  const compte = comptes.find((x) => x.id === valeur.compteId) || null;
  const modes = modesDuCompte(compte);
  return (
    <>
      <Champ libelle="Compte de trésorerie" requis aide={lecture?.erreur ? lecture.erreur.message : lecture?.donnees && !comptes.length ? 'Aucun compte de trésorerie actif.' : undefined}>
        <select className="rh-champ-saisie" value={valeur.compteId} onChange={(e) => {
          const t = comptes.find((x) => x.id === e.target.value);
          onChange({ compteId: e.target.value, mode: t && t.type === 'CAISSE' ? 'ESPECES' : 'VIREMENT', cheque: '' });
        }}>
          <option value="">Choisir…</option>
          {comptes.map((x) => <option key={x.id} value={x.id}>{x.libelle} ({x.type === 'CAISSE' ? 'caisse' : 'banque'})</option>)}
        </select>
      </Champ>
      <Champ libelle="Mode" requis>
        <select className="rh-champ-saisie" value={valeur.mode} onChange={(e) => onChange({ ...valeur, mode: e.target.value, cheque: '' })} disabled={!compte}>
          {modes.map((x) => <option key={x} value={x}>{MODES_PAIEMENT[x]}</option>)}
        </select>
      </Champ>
      {compte && valeur.mode === 'CHEQUE' && <ChoixCheque compte={compte} valeur={valeur.cheque} onChange={(cheque) => onChange({ ...valeur, cheque })} />}
    </>
  );
}

/** État initial du choix. */
export const choixInitial = () => ({ compteId: '', mode: 'VIREMENT', cheque: '' });
/** Saisie → objets utiles : { compte, mode, cheque }. */
export const lireChoix = (comptes, v) => ({ compte: comptes.find((x) => x.id === v.compteId) || null, mode: v.mode, cheque: v.cheque });
